import { useEffect, useState, useCallback, useMemo } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtMYR, fmtL, fmtDate, prodLabel } from "@/lib/format";
import { DateInput } from "@/components/DateInput";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Gauge, Truck, Wallet, Landmark, Loader2, Pencil, Trash2, ClipboardCheck, AlertTriangle, Coins, FileSpreadsheet, ClipboardPaste, TrendingUp } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const inp = "mt-1.5 bg-[#0B0F17] border-[#374151] text-white";

const FIELDS = {
  meter: [["date", "date"], ["opening", "number"], ["closing", "number"], ["retail_price", "number"]],
  delivery: [["date", "date"], ["product", "text"], ["liters", "number"], ["invoice_cost", "number"]],
  cash: [["date", "date"], ["cash_collected", "number"], ["card_collected", "number"], ["qr_collected", "number"], ["mart_sales", "number"], ["notes", "text"]],
  banking: [["date", "date"], ["total_bankin", "number"], ["notes", "text"]],
};
const COLS = {
  meter: ["date", "product", "opening", "closing", "liters_sold", "retail_price", "revenue"],
  delivery: ["date", "product", "liters", "invoice_cost"],
  cash: ["date", "cash_collected", "card_collected", "qr_collected", "mart_sales", "notes"],
  banking: ["date", "expected", "total_bankin", "difference", "status", "notes"],
};
const KEYMAP = { meter: "meter_readings", delivery: "deliveries", cash: "cash_entries", banking: "banking" };
const FIXED_MARGIN = { RON95: 0.15, DIESEL: 0.128, EURO5: 0.128 };
const money = new Set(["revenue", "retail_price", "invoice_cost", "cash_collected", "card_collected", "qr_collected", "mart_sales", "expected", "total_bankin", "difference"]);
const PAGE_SIZE = 30;

function Pager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const pages = [];
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="summary-pagination">
      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}
        data-testid="summary-page-prev" className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white h-8 px-2.5">Prev</Button>
      {start > 1 && <span className="text-[#6B7280] px-1">1 …</span>}
      {pages.map((p) => (
        <button key={p} onClick={() => onChange(p)} data-testid={`summary-page-${p}`}
          className={`h-8 min-w-8 px-2.5 rounded-md text-sm font-medium border transition-colors ${p === page ? "bg-emerald-500 text-black border-emerald-500" : "border-[#374151] text-white/80 hover:bg-[#1F2937]"}`}>{p}</button>
      ))}
      {end < totalPages && <span className="text-[#6B7280] px-1">… {totalPages}</span>}
      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}
        data-testid="summary-page-next" className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white h-8 px-2.5">Next</Button>
    </div>
  );
}

export default function Summary() {
  const { stationId, setStationId: setStation, stations } = useStation();
  const station = stationId === "all" ? (stations[0]?.id || "") : stationId;
  const [product, setProduct] = useState("all");
  const [products, setProducts] = useState([]);
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [editing, setEditing] = useState(null); // {kind, row}
  const [saving, setSaving] = useState(false);
  const [mForm, setMForm] = useState({ date: "", product: "RON95", opening: "", closing: "" });
  const [sForm, setSForm] = useState({ date: "", product: "RON95", volume: "", price: "" });
  const [dForm, setDForm] = useState({ date: "", product: "RON95", liters: "", invoice_cost: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkProduct, setBulkProduct] = useState("RON95");
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [mBulkOpen, setMBulkOpen] = useState(false);
  const [mBulkProduct, setMBulkProduct] = useState("RON95");
  const [mBulkText, setMBulkText] = useState("");
  const [mBulkSaving, setMBulkSaving] = useState(false);
  const [tab, setTab] = useState("sales");
  const [page, setPage] = useState(1);

  const addMeter = async () => {
    if (!station || !mForm.date) return toast.error("Pick a station and date");
    try {
      await api.post("/manual-meter", { station_id: station, date: mForm.date, product: mForm.product, opening: Number(mForm.opening || 0), closing: Number(mForm.closing || 0) });
      toast.success("Meter reading added"); setMForm({ ...mForm, opening: "", closing: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const addSale = async () => {
    if (!station || !sForm.date) return toast.error("Pick a station and date");
    try {
      await api.post("/manual-meter", { station_id: station, date: sForm.date, product: sForm.product, opening: 0, closing: Number(sForm.volume || 0), retail_price: Number(sForm.price || 0) });
      toast.success("Sale added"); setSForm({ ...sForm, volume: "", price: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const deleteSale = async () => {
    if (!station || !sForm.date) return toast.error("Pick a station and date to delete a sale");
    const rows = (entries?.meter_readings || []).filter((r) =>
      r.date === sForm.date && r.product === sForm.product &&
      (!Number(sForm.volume) || Math.abs((r.liters_sold || 0) - Number(sForm.volume)) < 0.01));
    if (!rows.length) return toast.error("No matching sale found for that date/product");
    if (!window.confirm(`Delete ${rows.length} sale(s) on ${fmtDate(sForm.date)} · ${sForm.product}?`)) return;
    try {
      for (const r of rows) await api.delete(`/entries/meter/${r.id}`);
      toast.success(`Deleted ${rows.length} sale(s)`); setSForm({ ...sForm, volume: "", price: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const addDelivery = async () => {
    if (!station || !dForm.date) return toast.error("Pick a station and date");
    if (!Number(dForm.liters)) return toast.error("Enter litres delivered");
    try {
      await api.post("/deliveries", { station_id: station, date: dForm.date, product: dForm.product, liters: Number(dForm.liters || 0), invoice_cost: Number(dForm.invoice_cost || 0) });
      toast.success("Delivery added"); setDForm({ ...dForm, liters: "", invoice_cost: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const clearMeter = async () => {
    if (!window.confirm(`Delete ALL meter readings for this station${month ? ` in ${month}` : ""}? You can reload later via "Load Jun–Aug 2026 data".`)) return;
    try { const { data } = await api.post(`/admin/clear-meter?station_id=${station}${month ? `&month=${month}` : ""}`); toast.success(`Deleted ${data.deleted} readings`); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const importBulk = async () => {
    if (!station) return toast.error("Pick a station");
    const rows = bulkText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [date, volume, price] = l.split(/[\t,;]+/).map((s) => s.trim());
      return { date, product: bulkProduct, opening: 0, closing: Number(volume || 0), retail_price: price ? Number(price) : null };
    }).filter((r) => r.date && r.closing);
    if (!rows.length) return toast.error("Nothing to import — paste one line per day: date, volume, price");
    setBulkSaving(true);
    try {
      const { data } = await api.post("/manual-meter/bulk", { station_id: station, rows });
      if (data.errors?.length) toast.warning(`Imported ${data.inserted}, skipped ${data.errors.length}: ${data.errors[0]}`);
      else toast.success(`Imported ${data.inserted} rows`);
      setBulkText(""); setBulkOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setBulkSaving(false);
  };

  const exportSales = async () => {
    if (!station || !month) return toast.error("Pick a station and month");
    setExporting(true);
    try {
      const q = product !== "all" ? `&product=${product}` : "";
      const res = await api.get(`/daily-sales/export?month=${month}&station_id=${station}${q}`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = u; a.download = `daily-sales-${month}.xlsx`; a.click(); URL.revokeObjectURL(u);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setExporting(false);
  };

  const importMeterBulk = async () => {
    if (!station) return toast.error("Pick a station");
    const rows = mBulkText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [date, opening, closing] = l.split(/[\t,;]+/).map((s) => s.trim());
      return { date, product: mBulkProduct, opening: Number(opening || 0), closing: Number(closing || 0), retail_price: null };
    }).filter((r) => r.date && (r.closing || r.opening));
    if (!rows.length) return toast.error("Nothing to import — paste one line per day: date, opening, closing");
    setMBulkSaving(true);
    try {
      const { data } = await api.post("/manual-meter/bulk", { station_id: station, rows });
      if (data.errors?.length) toast.warning(`Imported ${data.inserted}, skipped ${data.errors.length}: ${data.errors[0]}`);
      else toast.success(`Imported ${data.inserted} readings`);
      setMBulkText(""); setMBulkOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setMBulkSaving(false);
  };

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/data-months").then((r) => { const m = r.data || []; setMonth(m.length ? m[m.length - 1] : new Date().toISOString().slice(0, 7)); });
  }, []);

  useEffect(() => {
    if (!station || !month) return;
    api.get(`/reconcile-alerts?month=${month}&station_id=${station}`).then((r) => setAlerts(r.data.alerts || [])).catch(() => setAlerts([]));
  }, [station, month]);

  const load = useCallback(() => {
    if (!station || !month) return;
    setLoading(true);
    api.get(`/entries?station_id=${station}&month=${month}`)
      .then((r) => setEntries(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [station, month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [tab, month, product, station]);

  const rowsFor = (kind) => {
    let rows = entries?.[KEYMAP[kind]] || [];
    if (month) rows = rows.filter((r) => (r.date || "").startsWith(month));
    if (product !== "all" && (kind === "meter" || kind === "delivery")) rows = rows.filter((r) => r.product === product);
    return rows;
  };

  const salesTotals = useMemo(() => {
    let rows = entries?.meter_readings || [];
    if (month) rows = rows.filter((r) => (r.date || "").startsWith(month));
    if (product !== "all") rows = rows.filter((r) => r.product === product);
    let liters = 0, sales = 0, profit = 0;
    rows.forEach((r) => {
      const vol = r.liters_sold || 0;
      liters += vol; sales += vol * (r.retail_price || 0);
      profit += vol * (FIXED_MARGIN[r.product] ?? 0);
    });
    return { liters, sales, profit, days: new Set(rows.map((r) => r.date)).size };
  }, [entries, month, product]);

  const openEdit = (kind, row) => {
    const form = {};
    FIELDS[kind].forEach(([f]) => { form[f] = row[f] ?? ""; });
    setEditing({ kind, id: row.id, product: row.product, form });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/entries/${editing.kind}/${editing.id}`, editing.form);
      toast.success("Entry corrected");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSaving(false);
  };

  const del = async (kind, id) => {
    try { await api.delete(`/entries/${kind}/${id}`); toast.success("Entry deleted"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const fmtCell = (col, v) => {
    if (col === "date") return fmtDate(v);
    if (col === "liters_sold" || col === "liters") return fmtL(v || 0);
    if (money.has(col)) return fmtMYR(v || 0);
    return v ?? "—";
  };

  const tabDef = [
    ["meter", "Meter", Gauge], ["delivery", "Delivery", Truck],
    ["cash", "Cash", Wallet], ["banking", "Banking", Landmark],
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Manager · Corrections</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-8 h-8 text-emerald-400" /> Summary &amp; Corrections
          </h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Review and fix input errors in meter, delivery, cash &amp; banking records.</p>
        </div>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <Label className="text-[#9CA3AF] text-xs">Month</Label>
            <Input type="month" data-testid="summary-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Station</Label>
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger data-testid="summary-station-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-48"><SelectValue placeholder="Station" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                {stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs">Product</Label>
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger data-testid="summary-product-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                <SelectItem value="all">All Products</SelectItem>
                {products.map((p) => <SelectItem key={p} value={p}>{prodLabel(p)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.05]" data-testid="reconcile-alerts">
          <button onClick={() => setShowAlerts((s) => !s)} data-testid="reconcile-alerts-toggle"
            className="w-full flex items-center justify-between p-4 text-left">
            <span className="font-[Outfit] font-semibold text-amber-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> {alerts.length} day{alerts.length === 1 ? "" : "s"} need attention · {month}
            </span>
            <span className="text-amber-300 text-sm">{showAlerts ? "Hide" : "Show"}</span>
          </button>
          {showAlerts && (
            <div className="px-4 pb-4 space-y-2 max-h-64 overflow-y-auto">
              {alerts.map((a) => (
                <div key={a.date} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3 text-sm border-b border-amber-500/10 pb-2" data-testid={`alert-${a.date}`}>
                  <span className="metric-value text-white/90 w-28 shrink-0">{fmtDate(a.date)}</span>
                  <span className="text-amber-300">{a.issues.join(" · ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading || !entries ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#111827] border border-[#1F2937]">
            <TabsTrigger value="sales" data-testid="summary-tab-sales"><Coins className="w-4 h-4 mr-1.5" /> Daily Sales</TabsTrigger>
            {tabDef.map(([k, label, Icon]) => (
              <TabsTrigger key={k} value={k} data-testid={`summary-tab-${k}`}><Icon className="w-4 h-4 mr-1.5" /> {label}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="sales">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4" data-testid="sales-totals-card">
              <div className={`${card} p-4`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#9CA3AF]">Month-to-date · {month || "—"}</div>
                <div className="metric-value text-2xl font-bold text-white mt-1" data-testid="sales-total-liters">{fmtL(salesTotals.liters)}</div>
                <div className="text-xs text-[#9CA3AF] mt-0.5">litres sold · {salesTotals.days} day{salesTotals.days === 1 ? "" : "s"}</div>
              </div>
              <div className={`${card} p-4`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#9CA3AF]">Sales (RM)</div>
                <div className="metric-value text-2xl font-bold text-white mt-1" data-testid="sales-total-sales">{fmtMYR(salesTotals.sales)}</div>
                <div className="text-xs text-[#9CA3AF] mt-0.5">volume × pump price</div>
              </div>
              <div className={`${card} p-4`}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400/80 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Net Profit (RM)</div>
                <div className="metric-value text-2xl font-bold text-emerald-400 mt-1" data-testid="sales-total-profit">{fmtMYR(salesTotals.profit)}</div>
                <div className="text-xs text-[#9CA3AF] mt-0.5">at fixed margin</div>
              </div>
              <div className={`${card} p-4 flex flex-col justify-center gap-2`}>
                <Button onClick={exportSales} disabled={exporting} data-testid="sales-export-button" className="bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white font-semibold">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} Download Excel
                </Button>
                <Button onClick={() => setBulkOpen((v) => !v)} variant="outline" data-testid="sales-bulk-toggle" className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
                  <ClipboardPaste className="w-4 h-4 mr-2" /> {bulkOpen ? "Close paste" : "Bulk paste"}
                </Button>
              </div>
            </div>

            {bulkOpen && (
              <div className={`${card} p-5 mb-4`} data-testid="sales-bulk-form">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-3">
                  <div className="flex-1">
                    <Label className="text-[#9CA3AF] text-xs">Paste a whole month — one line per day: <span className="text-white/80 font-mono">date, volume, price</span> (price optional)</Label>
                    <Textarea data-testid="sales-bulk-textarea" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={8}
                      placeholder={"01/06/2026, 1200, 3.92\n02/06/2026, 1345.5, 3.92\n03/06/2026, 980, 3.92"}
                      className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white font-mono text-sm" />
                  </div>
                  <div className="sm:w-44">
                    <Label className="text-[#9CA3AF] text-xs">Product (all rows)</Label>
                    <Select value={bulkProduct} onValueChange={setBulkProduct}>
                      <SelectTrigger data-testid="sales-bulk-product" className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#111827] border-[#374151] text-white"><SelectItem value="RON95">RON95</SelectItem><SelectItem value="DIESEL">DIESEL</SelectItem><SelectItem value="EURO5">Euro 5 (B7)</SelectItem></SelectContent>
                    </Select>
                    <Button onClick={importBulk} disabled={bulkSaving} data-testid="sales-bulk-import" className="mt-3 w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                      {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Import rows
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-[#9CA3AF]">Separators accepted: comma, tab or semicolon. Leave price blank to use the weekly pump price for that date. Each line adds one sale.</p>
              </div>
            )}

            <div className={`${card} p-5 sm:p-6`}>
              <div className="mb-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end border-b border-[#1F2937] pb-4" data-testid="sales-add-form">
                <div><Label className="text-[#9CA3AF] text-xs">Date</Label><DateInput data-testid="sales-add-date" value={sForm.date} onChange={(iso) => setSForm({ ...sForm, date: iso })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Product</Label>
                  <Select value={sForm.product} onValueChange={(v) => setSForm({ ...sForm, product: v })}><SelectTrigger data-testid="sales-add-product" className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-[#111827] border-[#374151] text-white"><SelectItem value="RON95">RON95</SelectItem><SelectItem value="DIESEL">DIESEL</SelectItem><SelectItem value="EURO5">Euro 5 (B7)</SelectItem></SelectContent></Select></div>
                <div><Label className="text-[#9CA3AF] text-xs">Volume (L)</Label><Input type="number" step="0.01" data-testid="sales-add-volume" value={sForm.volume} onChange={(e) => setSForm({ ...sForm, volume: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                <div><Label className="text-[#9CA3AF] text-xs">Pump Price</Label><Input type="number" step="0.01" data-testid="sales-add-price" value={sForm.price} onChange={(e) => setSForm({ ...sForm, price: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                <div className="flex gap-2">
                  <Button onClick={addSale} data-testid="sales-add-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex-1">Add sale</Button>
                  <Button onClick={deleteSale} data-testid="sales-delete-button" variant="outline" className="border-red-500/40 text-red-400 bg-transparent hover:bg-red-500/10 flex-1">Delete sale</Button>
                </div>
              </div>
              {rowsFor("meter").length ? (() => {
                const rows = rowsFor("meter");
                let tv = 0, ts = 0, tp = 0;
                rows.forEach((r) => {
                  const vol = r.liters_sold || 0;
                  tv += vol; ts += vol * (r.retail_price || 0); tp += vol * (FIXED_MARGIN[r.product] ?? 0);
                });
                const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
                const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                const body = pageRows.map((r) => {
                  const vol = r.liters_sold || 0;
                  const price = r.retail_price || 0;
                  const sales = vol * price;
                  const profit = vol * (FIXED_MARGIN[r.product] ?? 0);
                  return (
                    <tr key={r.id} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]" data-testid={`sales-row-${r.id}`}>
                      <td className="py-2.5 pr-4 metric-value text-white/80">{fmtDate(r.date)}</td>
                      <td className="py-2.5 pr-4">{r.product}</td>
                      <td className="py-2.5 pr-4 metric-value">{fmtL(vol)}</td>
                      <td className="py-2.5 pr-4 metric-value">{fmtMYR(price)}</td>
                      <td className="py-2.5 pr-4 metric-value text-white">{fmtMYR(sales)}</td>
                      <td className="py-2.5 pr-4 metric-value text-emerald-400">{fmtMYR(profit)}</td>
                      <td className="py-2.5 pr-2 text-right">
                        <button onClick={() => { if (window.confirm(`Delete this sale (${fmtDate(r.date)} · ${r.product} · ${fmtL(vol)})?`)) del("meter", r.id); }}
                          data-testid={`sales-delete-${r.id}`} className="text-[#6B7280] hover:text-red-400" title="Delete sale">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                });
                return (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="summary-table-sales">
                        <thead>
                          <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                            {["date", "product", "volume (L)", "pump price", "sales (RM)", "net profit (RM)"].map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                            <th className="py-2 pr-2 font-medium text-right">actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {body}
                          <tr className="border-t-2 border-[#1F2937] font-bold" data-testid="summary-sales-total">
                            <td className="py-2.5 pr-4">TOTAL</td><td></td>
                            <td className="py-2.5 pr-4 metric-value">{fmtL(tv)}</td><td></td>
                            <td className="py-2.5 pr-4 metric-value text-white">{fmtMYR(ts)}</td>
                            <td className="py-2.5 pr-4 metric-value text-emerald-400">{fmtMYR(tp)}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                      <span className="text-xs text-[#9CA3AF]" data-testid="summary-sales-count">{rows.length} record{rows.length === 1 ? "" : "s"} · page {page}/{totalPages}</span>
                      <Pager page={page} totalPages={totalPages} onChange={setPage} />
                    </div>
                  </>
                );
              })() : <div className="text-sm text-[#9CA3AF] py-8 text-center">No sales records for this selection.</div>}
              <p className="text-xs text-[#9CA3AF] mt-3">Sales = volume × pump price. Net profit = volume × fixed margin (RON95 RM0.15/L, Diesel RM0.128/L).</p>
            </div>
          </TabsContent>

          {tabDef.map(([kind]) => (
            <TabsContent key={kind} value={kind}>
              <div className={`${card} p-5 sm:p-6`}>
                {kind === "meter" && (
                  <div className="mb-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end border-b border-[#1F2937] pb-4" data-testid="meter-add-form">
                    <div><Label className="text-[#9CA3AF] text-xs">Date</Label><DateInput data-testid="meter-add-date" value={mForm.date} onChange={(iso) => setMForm({ ...mForm, date: iso })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Product</Label>
                      <Select value={mForm.product} onValueChange={(v) => setMForm({ ...mForm, product: v })}><SelectTrigger data-testid="meter-add-product" className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-[#111827] border-[#374151] text-white"><SelectItem value="RON95">RON95</SelectItem><SelectItem value="DIESEL">DIESEL</SelectItem><SelectItem value="EURO5">Euro 5 (B7)</SelectItem></SelectContent></Select></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Opening</Label><Input type="number" step="0.01" data-testid="meter-add-opening" value={mForm.opening} onChange={(e) => setMForm({ ...mForm, opening: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Closing</Label><Input type="number" step="0.01" data-testid="meter-add-closing" value={mForm.closing} onChange={(e) => setMForm({ ...mForm, closing: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                    <div className="flex gap-2">
                      <Button onClick={addMeter} data-testid="meter-add-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold flex-1">Add</Button>
                      <Button onClick={() => setMBulkOpen((v) => !v)} variant="outline" data-testid="meter-bulk-toggle" className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white px-3"><ClipboardPaste className="w-4 h-4" /></Button>
                      <Button onClick={clearMeter} variant="outline" data-testid="meter-clear-button" className="border-red-500/40 text-red-400 bg-transparent hover:bg-red-500/10">Clear</Button>
                    </div>
                  </div>
                )}
                {kind === "meter" && mBulkOpen && (
                  <div className="mb-4 border-b border-[#1F2937] pb-4" data-testid="meter-bulk-form">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                      <div className="flex-1">
                        <Label className="text-[#9CA3AF] text-xs">Paste a whole month — one line per day: <span className="text-white/80 font-mono">date, opening, closing</span></Label>
                        <Textarea data-testid="meter-bulk-textarea" value={mBulkText} onChange={(e) => setMBulkText(e.target.value)} rows={8}
                          placeholder={"01/06/2026, 100000, 101200\n02/06/2026, 101200, 102545\n03/06/2026, 102545, 103525"}
                          className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white font-mono text-sm" />
                      </div>
                      <div className="sm:w-44">
                        <Label className="text-[#9CA3AF] text-xs">Product (all rows)</Label>
                        <Select value={mBulkProduct} onValueChange={setMBulkProduct}>
                          <SelectTrigger data-testid="meter-bulk-product" className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#111827] border-[#374151] text-white"><SelectItem value="RON95">RON95</SelectItem><SelectItem value="DIESEL">DIESEL</SelectItem><SelectItem value="EURO5">Euro 5 (B7)</SelectItem></SelectContent>
                        </Select>
                        <Button onClick={importMeterBulk} disabled={mBulkSaving} data-testid="meter-bulk-import" className="mt-3 w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                          {mBulkSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Import readings
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-[#9CA3AF] mt-2">Litres = closing − opening. Separators accepted: comma, tab or semicolon. Pump price is auto-filled from the weekly price for each date.</p>
                  </div>
                )}
                {kind === "delivery" && (
                  <div className="mb-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end border-b border-[#1F2937] pb-4" data-testid="delivery-add-form">
                    <div><Label className="text-[#9CA3AF] text-xs">Date</Label><DateInput data-testid="delivery-add-date" value={dForm.date} onChange={(iso) => setDForm({ ...dForm, date: iso })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Product</Label>
                      <Select value={dForm.product} onValueChange={(v) => setDForm({ ...dForm, product: v })}><SelectTrigger data-testid="delivery-add-product" className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white"><SelectValue /></SelectTrigger><SelectContent className="bg-[#111827] border-[#374151] text-white"><SelectItem value="RON95">RON95</SelectItem><SelectItem value="DIESEL">DIESEL</SelectItem><SelectItem value="EURO5">Euro 5 (B7)</SelectItem></SelectContent></Select></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Litres delivered</Label><Input type="number" step="0.01" data-testid="delivery-add-liters" value={dForm.liters} onChange={(e) => setDForm({ ...dForm, liters: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                    <div><Label className="text-[#9CA3AF] text-xs">Invoice cost (RM)</Label><Input type="number" step="0.01" data-testid="delivery-add-cost" value={dForm.invoice_cost} onChange={(e) => setDForm({ ...dForm, invoice_cost: e.target.value })} className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" /></div>
                    <Button onClick={addDelivery} data-testid="delivery-add-button" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">Add delivery</Button>
                  </div>
                )}
                {rowsFor(kind).length ? (() => {
                  const rows = rowsFor(kind);
                  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
                  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
                  return (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid={`summary-table-${kind}`}>
                          <thead>
                            <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                              {COLS[kind].map((c) => <th key={c} className="py-2 pr-4 font-medium">{c.replace(/_/g, " ")}</th>)}
                              <th className="py-2 pr-4 font-medium">last edit</th>
                              <th className="py-2 pr-4 font-medium text-right">actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageRows.map((row) => (
                              <tr key={row.id} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]" data-testid={`summary-row-${kind}-${row.id}`}>
                                {COLS[kind].map((c) => <td key={c} className="py-2.5 pr-4 metric-value text-white/90">{fmtCell(c, row[c])}</td>)}
                                <td className="py-2.5 pr-4 text-xs text-[#9CA3AF]" data-testid={`summary-editlog-${row.id}`}>{row.updated_by ? `${row.updated_by}${row.updated_at ? " · " + fmtDate(row.updated_at.slice(0, 10)) : ""}` : "—"}</td>
                                <td className="py-2.5 pr-2 text-right whitespace-nowrap">
                                  <button onClick={() => openEdit(kind, row)} data-testid={`summary-edit-${row.id}`} className="text-emerald-400 hover:text-emerald-300 mr-3"><Pencil className="w-4 h-4 inline" /></button>
                                  <button onClick={() => del(kind, row.id)} data-testid={`summary-delete-${row.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4 inline" /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                        <span className="text-xs text-[#9CA3AF]" data-testid={`summary-count-${kind}`}>{rows.length} record{rows.length === 1 ? "" : "s"} · page {page}/{totalPages}</span>
                        <Pager page={page} totalPages={totalPages} onChange={setPage} />
                      </div>
                    </>
                  );
                })() : <div className="text-sm text-[#9CA3AF] py-8 text-center">No {kind} records for this selection.</div>}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111827] border-[#374151] text-white">
          <DialogHeader><DialogTitle>Correct {editing?.kind} entry{editing?.product ? ` · ${editing.product}` : ""}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3" data-testid="summary-edit-form">
              {FIELDS[editing.kind].map(([f, type]) => (
                <div key={f}>
                  <Label className="text-[#9CA3AF] capitalize">{f.replace(/_/g, " ")}</Label>
                  <Input type={type} step={type === "number" ? "0.01" : undefined}
                    data-testid={`summary-field-${f}`} value={editing.form[f]}
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, [f]: e.target.value } })}
                    className={inp} />
                </div>
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">Cancel</Button>
                <Button onClick={saveEdit} disabled={saving} data-testid="summary-save-edit" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save correction
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
